// src/services/labelService.ts

import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { LabelRecord, LabelCreate, LabelHistory } from '../types/label.types';

/**
 * Converte documento Firestore para LabelRecord
 */
const convertFirestoreToLabel = (id: string, data: any): LabelRecord => {
  return {
    labelId: id,
    audioId: data.audioId,
    label: data.label,
    protocol_version: data.protocol_version,
    comment: data.comment,
    author_uid: data.author_uid,
    author_name: data.author_name,
    created_at: data.created_at?.toDate() || new Date(),
    replaces: data.replaces
  };
};

/**
 * Cria um novo rótulo clínico para um áudio
 */
export const createLabel = async (
  labelData: LabelCreate,
  authorUid: string,
  authorName: string,
  currentLabelId?: string
): Promise<LabelRecord> => {
  try {
    // ✅ CORREÇÃO: Usar coleção raiz /labels em vez de subcoleção
    const labelsCollection = collection(db, 'labels');

    const newLabel = {
      audioId: labelData.audioId,
      label: labelData.label,
      protocol_version: labelData.protocol_version,
      comment: labelData.comment || '',
      author_uid: authorUid,
      author_name: authorName,
      created_at: serverTimestamp(),
      replaces: currentLabelId || null
    };

    const docRef = await addDoc(labelsCollection, newLabel);

    return {
      labelId: docRef.id,
      audioId: labelData.audioId,
      label: labelData.label,
      protocol_version: labelData.protocol_version,
      comment: labelData.comment,
      author_uid: authorUid,
      author_name: authorName,
      created_at: new Date(),
      replaces: currentLabelId
    };
  } catch (error) {
    console.error('Erro ao criar rótulo:', error);
    throw new Error('Falha ao salvar rótulo no Firestore');
  }
};

/**
 * Busca o histórico completo de rótulos de um áudio
 */
export const getLabelHistory = async (audioId: string): Promise<LabelHistory> => {
  try {
    // ✅ CORREÇÃO: Usar coleção raiz /labels com filtro por audioId
    const labelsCollection = collection(db, 'labels');
    const labelsQuery = query(
      labelsCollection,
      where('audioId', '==', audioId),
      orderBy('created_at', 'desc')
    );
    
    const snapshot = await getDocs(labelsQuery);

    const labels: LabelRecord[] = [];
    snapshot.forEach((doc) => {
      labels.push(convertFirestoreToLabel(doc.id, doc.data()));
    });

    return {
      labels,
      currentLabel: labels.length > 0 ? labels[0] : undefined
    };
  } catch (error) {
    console.error('Erro ao buscar histórico de rótulos:', error);
    throw new Error('Falha ao buscar histórico de rótulos');
  }
};

/**
 * Busca apenas o rótulo mais recente de um áudio
 */
export const getCurrentLabel = async (audioId: string): Promise<LabelRecord | null> => {
  try {
    const history = await getLabelHistory(audioId);
    return history.currentLabel || null;
  } catch (error) {
    console.error('Erro ao buscar rótulo atual:', error);
    return null;
  }
};

/**
 * Valida se o usuário pode criar/editar rótulos
 */
export const canUserLabel = (userRole: string): boolean => {
  const allowedRoles = ['REVISOR_CLINICO', 'ADMIN', 'admin', 'reviewer'];
  return allowedRoles.includes(userRole);
};
